/**
 * Cleanup Duplicate Templates Utility
 * 
 * Run this once to remove duplicate templates from Firestore.
 */

import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export async function cleanupDuplicateTemplates(deleteAll: boolean = false): Promise<{ 
  success: boolean; 
  deleted: number; 
  kept: number;
  error?: string;
}> {
  try {
    console.log('[CleanupTemplates] Starting...');
    
    const templatesRef = collection(db, 'tripTemplates');
    const snapshot = await getDocs(templatesRef);
    
    const templates = snapshot.docs.map(doc => ({
      id: doc.id,
      title: doc.data().title,
      createdAt: doc.data().createdAt?.toDate() || new Date(),
    }));
    
    console.log(`[CleanupTemplates] Found ${templates.length} templates`);
    
    let deletedCount = 0;
    let keptCount = 0;
    
    if (deleteAll) {
      // Delete ALL templates for a fresh start
      console.log('[CleanupTemplates] Deleting ALL templates for fresh re-seed...');
      for (const template of templates) {
        await deleteDoc(doc(db, 'tripTemplates', template.id));
        console.log(`[CleanupTemplates] Deleted "${template.title}"`);
        deletedCount++;
      }
    } else {
      // Group by title
      const byTitle = new Map<string, typeof templates>();
      templates.forEach(template => {
        const existing = byTitle.get(template.title) || [];
        existing.push(template);
        byTitle.set(template.title, existing);
      });
      
      // For each title, keep the oldest (first created), delete the rest
      for (const [title, duplicates] of byTitle.entries()) {
        if (duplicates.length > 1) {
          console.log(`[CleanupTemplates] Found ${duplicates.length} duplicates of "${title}"`);
          
          // Sort by creation date (oldest first)
          duplicates.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
          
          // Keep the first one, delete the rest
          const toKeep = duplicates[0];
          const toDelete = duplicates.slice(1);
          
          console.log(`[CleanupTemplates] Keeping template created at ${toKeep.createdAt.toISOString()}`);
          keptCount++;
          
          for (const template of toDelete) {
            await deleteDoc(doc(db, 'tripTemplates', template.id));
            console.log(`[CleanupTemplates] Deleted duplicate created at ${template.createdAt.toISOString()}`);
            deletedCount++;
          }
        } else {
          keptCount++;
        }
      }
    }
    
    console.log(`[CleanupTemplates] Complete! Deleted ${deletedCount}, kept ${keptCount}`);
    
    // Clear cache
    const { clearTemplatesCache } = await import('@/services/tripTemplates');
    await clearTemplatesCache();
    
    return { success: true, deleted: deletedCount, kept: keptCount };
  } catch (error) {
    console.error('[CleanupTemplates] Error:', error);
    return { 
      success: false, 
      deleted: 0,
      kept: 0,
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

